import { TSearchRequest } from '@/components';
import {
	MAX_LENGTH_DETAIL_TITLE,
	PageTypeEnum,
	ResidenceEnum,
	ResidenceLabel,
	UpdateFieldObjectNameEnum,
	VisaTimeEnum,
	VisaTimeLabel,
} from '@/constant';
import { TGetDataSearchFilter, TUpdateFieldResponse } from '@/services';
import { TDataUpdateFieldsRequest } from '@/stores';

import { Dayjs, isDayjs } from 'dayjs';
import { trim } from 'ramda';
import {
	isBoolean,
	isNilOrEmpty,
	isNotNilOrEmpty,
	isString,
} from 'ramda-adjunct';

import { formatDate } from './date.util';

export const formatSearchValue = (value: any) => {
	if (isString(value)) {
		return trim(value);
	}
	if (isBoolean(value)) {
		return 1;
	}
	return value;
};

export const maskingEmail = (email: string) => {
	if (isNilOrEmpty(email)) return '';
	const [name, domain] = email.split('@');
	if (name?.length <= 3) return email;
	const maskedName = name?.slice(0, -3) + '***';
	const maskedEmail = maskedName + '@' + domain;
	return maskedEmail;
};

export const maskingName = (name: string) => {
	if (isNilOrEmpty(name)) return '';
	const { length } = name;
	if (length === 1) return name;
	const lengthMask = length - 2;
	const maskedName =
		name[0] +
		'*'.repeat(lengthMask || 1) +
		(lengthMask ? name?.[length - 1] : '');
	return maskedName;
};

export const diplayAddress = (
	zipcode?: string,
	address?: string,
	detailAddress?: string
) => {
	return [zipcode, address, detailAddress].filter(Boolean).join(' - ');
};

export const removeNilOrEmptyObj = (obj: any) => {
	let newObj = {};
	Object.keys(obj)?.forEach((element) => {
		if (isNotNilOrEmpty(obj[element]))
			newObj = {
				...newObj,
				[element]: obj[element],
			};
	});
	return newObj;
};

export const emptyToNullInObject = (obj: any) => {
	const newObj = { ...obj }; // Create a copy of the object
	for (const key in newObj) {
		if (newObj[key] === '' || newObj[key] === undefined) {
			newObj[key] = null;
		}
	}
	return newObj;
};

export const formatSearchParams = <T>(
	searchParams: URLSearchParams,
	key: string
) => {
	return searchParams && searchParams.get(key)
		? (JSON.parse(searchParams.get(key) || '') as T)
		: {};
};

export const allowOnlyNumeric = (text: string) => {
	if (!text) return '';
	return text.replace(/[^\d]/g, '');
};

export const allowOnlyNumericAndDot = (text: string) => {
	if (!text) return '';
	return text.replace(/[^\d.]/g, '');
};

export const formatNumberSpecialCharacter = (text: string) => {
	if (!text) return '';

	return text.replace(/[^0-9()-@!#$%&()_';":?/>.<,\\|{}~`^\]\\]/g, '');
};

export const allowNumericSomeChar = (text: string) => {
	if (!text) return '';
	return text.replace(/[^+\-\d()]/g, '');
};

export const allowEnglishKoreanNumber = (text: string) => {
	if (!text) return '';
	const regexPattern =
		/[^a-zA-Z0-9\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3\s]/g;
	return text.replace(regexPattern, '');
};

export const allowEnglish = (text: string) => {
	if (!text) return '';
	const regexPattern = /[^a-zA-Z\s]/g;
	return text.replace(regexPattern, '');
};

export const allowEnglishKorean = (text: string, maxLength?: number) => {
	if (!text) return '';
	const regexPattern = /[^a-zA-Z\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3\s]/g;
	const formattedText = text.replace(regexPattern, '');

	if (maxLength && formattedText.length >= maxLength) {
		return formattedText.slice(0, maxLength);
	}

	return formattedText;
};

export const allowEnglishKoreanNumberPeriod = (text: string) => {
	if (!text) return '';
	const regexPattern =
		/[^a-zA-Z\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3\s,·]/g;
	return text.replace(regexPattern, '');
};

export const removeFileNameInvalidChars = (
	text: string,
	{ maxLength, symbol }: { maxLength?: number; symbol?: string } = {}
) => {
	if (!text) return '';

	const regexPattern = /[\\/:*?"“<>|]/g;
	const formattedText = text.replace(regexPattern, symbol || '');

	if (maxLength && formattedText.length >= maxLength) {
		return formattedText.slice(0, maxLength);
	}

	return formattedText;
};

export const allowEnglishKoreanNumberCommaDash = (
	text: string,
	maxLength?: number
) => {
	if (!text) return '';

	const regexPattern =
		/[^a-zA-Z\u3131-\u314e\u314f-\u3163\uac00-\ud7a3\d\s-,]/g;
	const formattedText = text.replace(regexPattern, '');

	if (maxLength && formattedText.length >= maxLength) {
		return formattedText.slice(0, maxLength);
	}

	return formattedText;
};

export const allowKorean = (text: string, maxLength?: number) => {
	if (!text) return '';

	const regexPattern = /[^\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3\s]/g;
	const formattedText = text.replace(regexPattern, '');

	if (maxLength && formattedText.length >= maxLength) {
		return formattedText.slice(0, maxLength);
	}

	return formattedText;
};

export const allowEnglishKoreanNumberBracketDash = (
	text: string,
	maxLength?: number
) => {
	if (!text) return '';

	const regexPattern =
		/[^a-zA-Z\u3131-\u314e\u314f-\u3163\uac00-\ud7a3\d\s-()]/g;
	const formattedText = text.replace(regexPattern, '');

	if (maxLength && formattedText.length >= maxLength) {
		return formattedText.slice(0, maxLength);
	}

	return formattedText;
};

export const allowOnlyEnKrNumberCommaPlusBracketDash = (text: string) => {
	if (!text) return '';
	const regexPattern =
		/[^a-zA-Z\u3131-\u314e\u314f-\u3163\uac00-\ud7a3\d\s()\-+,]/g;
	return text.replace(regexPattern, '');
};

export const formatPassportNo = (text: string) => {
	if (!text) return '';
	const regexPattern = /[^a-zA-Z0-9-]/g;
	return text.replace(regexPattern, '').toLocaleUpperCase();
};

export const formatForeignNo = (text: string) => {
	if (!text) return '';
	const regexPattern = /[^0-9-]/g;
	return text.replace(regexPattern, '');
};

export const formatAddressHometown = (text: string) => {
	if (!text) return '';
	const regexPattern = /[!@#$%^&*._=\[\]{};:'"\\|<>/?~`]/g;
	return text.replace(regexPattern, '');
};

export const formatPhoneNumber = (text: string) => {
	if (!text) return '';
	const regexPattern = /[^\d()+\-]/g;
	return text.replace(regexPattern, '');
};

export const allowOnlyEnKrCharacter = (text: string) => {
	if (!text) return '';
	const regexPattern =
		/[^a-zA-Z\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3|!@#$%^&*()_-~`'";:/?<>,.\\|{}[\]\·+=\s]/g;
	return text.replace(regexPattern, '');
};

export const allowOnlyEnUpperCaseNumberDash = (text: string) => {
	if (!text) return '';
	const regexPattern = /[^A-Z0-9-]/g;
	return text.replace(regexPattern, '');
};

export const allowOnlyEnKrNumberSpecialChars = (text: string) => {
	if (!text) return '';
	const regexPattern =
		/[^a-zA-Z\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3\d\s!’@#$%^&*()-_=+×÷=[\]{};:'",.<>/?\\|`~]/g;
	return text.replace(regexPattern, '');
};

export const allowUrlWebsite = (text: string) => {
	if (!text) return '';
	const regexPattern =
		/[^a-zA-Z\u3131-\u314e|\u314f-\u3163|\uac00-\ud7a3\d\s:/?#[\]@!$&'()*+,;=\.]/g;
	return text.replace(regexPattern, '');
};

export const allowNumberDot = (text: string) => {
	if (!text) return '';
	const regexPattern = /[^\d.]/g;
	return text.replace(regexPattern, '');
};

// get second, third element of phone number if correct format
export const getSecondThirdPhoneElement = (phone: string = '') => {
	const regexPattern = /^(\d{2,3})-(\d{4})-(\d{4})$/;
	const match = phone?.match(regexPattern);
	return match ? [match[2], match[3]] : [];
};

export const checkPasswordContainsSecondThirdPhoneElement = (
	password: string,
	...phone: string[]
) => {
	let piecesOfPhone: string[] = [];
	phone?.forEach((element) => {
		const match = getSecondThirdPhoneElement(element);
		piecesOfPhone = [...piecesOfPhone, ...match];
	});
	return piecesOfPhone?.every((piece) => !password?.includes(piece));
};

interface JsonObject {
	[key: string]: string | number | boolean; // Adjust types as necessary
}
export const jsonToUrlParams = (json: JsonObject): string => {
	return Object.keys(json)
		.map((key) => encodeURIComponent(key) + '=' + encodeURIComponent(json[key]))
		.join('&');
};

export const showDataGridWithSymbol = (
	a?: string | number,
	b?: string | number,
	symbol?: string,
	empty?: boolean
) =>
	isNotNilOrEmpty(a) || isNotNilOrEmpty(b)
		? `${isNotNilOrEmpty(a) ? a : '-'} ${symbol || '/'} ${isNotNilOrEmpty(b) ? b : '-'}`
		: empty
			? ''
			: '-';

/**
 * Nối nhiều chuỗi với một ký tự phân cách
 * @param strings mảng các chuỗi cần nối
 * @param symbol ký tự phân cách, mặc định là dấu gạch ngang '_'
 * @param empty nếu tất cả các chuỗi đều trống, trả về chuỗi rỗng nếu empty=true, ngược lại trả về '-'
 * @returns chuỗi đã được nối
 */
export const joinMultipleStrings = (
	strings: Array<string | number | undefined | null>,
	symbol: string = '_',
	empty: boolean = false
): string => {
	const validStrings = strings
		.filter((s) => isNotNilOrEmpty(s))
		.map((s) => String(s));

	if (validStrings.length === 0) {
		return empty ? '' : '-';
	}

	return validStrings.join(symbol);
};

export const generateNameArray = (
	arrayName: Array<{ [key: string]: any }>,
	key: string = 'name',
	suffix: string = '명'
): string => {
	if (!arrayName.length) {
		return '';
	}
	let text = String(arrayName[0][key]);
	if (arrayName.length > 1) {
		text += ` 외 ${arrayName.length - 1}${suffix}`;
	}

	return text;
};

export const convertObjectToUrlParams = <T extends Record<string, unknown>>(
	obj: T
) => {
	const urlParams = new URLSearchParams();
	Object.keys(obj).forEach((key) => {
		if (obj[key] !== undefined)
			urlParams.append(key, obj[key]?.toString() || '');
	});
	return urlParams;
};

export const toCamelCase = (str?: string) => {
	if (!str) return '';
	return str
		.toLowerCase() // Convert the entire string to lowercase
		.replace(/[-_](.)/g, (match, group) => group.toUpperCase()) // Convert characters after hyphens or underscores to uppercase
		.replace(/^(.)/, (match, group) => group.toLowerCase()); // Ensure the first character is lowercase
};

export const camelToSnakeUppercase = (camelStr?: string) => {
	if (!camelStr) return '';
	return camelStr
		.replace(/([a-z])([A-Z])/g, '$1_$2')
		.replace(/([a-z])([0-9])/g, '$1_$2')
		.replace(/([A-Z])([0-9])/g, '$1_$2')
		.toUpperCase();
};

export const convertFieldUndefine = <T extends object>(obj: T) => {
	let newObj: T = {} as T;
	Object.entries(obj).map(([key, val]: [string, string | Dayjs | number]) => {
		if (Object.hasOwn(obj, key) && val) {
			if (isDayjs(val)) {
				newObj = {
					...newObj,
					[key]: formatDate(val),
				};
			} else {
				newObj = {
					...newObj,
					[key]: val,
				};
			}
		}
	});
	return newObj;
};

/**
 * Function checks an object and returns an empty array if the object is empty
 * or has a 'searchTypes' property that is an empty array.
 * @param {object} obj - The object to check.
 * @returns {object | Array} - Returns an empty array if conditions are met, otherwise returns the original object.
 */
export const returnEmptyIfSearchTypeInvalid = (
	obj: Partial<TGetDataSearchFilter>
): object | Array<any> => {
	if (
		Object.keys(obj).length === 0 ||
		(Object.hasOwn(obj, 'searchTypes') &&
			Array.isArray(obj?.searchTypes) &&
			obj.searchTypes.length === 0)
	) {
		return [];
	} else {
		return obj;
	}
};

export const convertCamelToSnakeCaseWithoutTableName = (
	camelStr: string,
	tableName: string,
	uppercase?: boolean
): string => {
	if (!camelStr || !tableName) return '';
	const snakeCase = camelStr
		.replace(/([a-z])([A-Z])/g, '$1_$2')
		.replace(/([a-z])([0-9])/g, '$1_$2')
		.replace(/([A-Z])([0-9])/g, '$1_$2')
		.toLowerCase(); // Ensure snake case and lowercase

	// Handle table names like USER_TABLE_TABLE_TABLE by converting to snake case and lowercase
	const tableNameSnakeCase = tableName.toLowerCase();

	// Replace only the first occurrence of the table name
	let firstOccurrenceRemoved = snakeCase.replace(
		new RegExp(`^${tableNameSnakeCase}_?`, ''),
		''
	);

	// Return result with multiple underscores handled
	let result = firstOccurrenceRemoved
		.replace(/^_+|_+$/g, '')
		.replace(/_+/g, '_');

	// Return the result, converting to uppercase if needed
	return uppercase ? result.toUpperCase() : result;
};

export const removeTableNameFromString = (
	str: string,
	tableName: string
): string => {
	if (!str || !tableName) return '';

	const tableNameLower = tableName.toLowerCase();

	const regex = new RegExp(tableNameLower, 'gi');

	const result = str.replace(regex, '').trim();

	return result.charAt(0).toLowerCase() + result.slice(1);
};

export const formatDataUpdateList = (
	data: TDataUpdateFieldsRequest['data'],
	tableName: string
) => {
	let _data = { id: data.id };
	Object.keys(data).map((e: string) => {
		if (e !== 'id') {
			let key = convertCamelToSnakeCaseWithoutTableName(e, tableName);
			_data = {
				..._data,
				[key]: isNotNilOrEmpty(data[e]) ? data[e] : null,
			};
		}
	});

	return _data;
};

export const formatDataUpdateFields = (
	prefix: UpdateFieldObjectNameEnum,
	fields: TUpdateFieldResponse[]
) => {
	let result = {} as Record<string, string | null>;

	fields?.forEach((item) => {
		const key = toCamelCase(`${prefix ? prefix + '_' : ''}${item?.fieldEnum}`);

		result = {
			...result,
			[key]: item?.value,
		};
	});

	return result;
};

export const getCodeInResidenceName = (residenceName: string) => {
	if (!residenceName) return '';
	let arrStr = residenceName.split(/[()]/);
	return arrStr[1] && isNotNilOrEmpty(arrStr[1]) ? arrStr[1] : '';
};

export const filterByPrefixKeyWithFormat = <T extends object>(
	obj: T,
	prefix: PageTypeEnum
) => {
	const result = {} as T;
	for (const key in obj) {
		if (key.startsWith(toCamelCase(prefix))) {
			result[key] = obj[key];
		}
	}

	return result;
};

export const putKeysOutFromSearchTypes = ({
	keys,
	searchTypes,
	keySearchNew = 'searchTypes',
}: {
	keys: string[];
	searchTypes: TSearchRequest['searchTypes'];
	keySearchNew?: string;
}) => {
	if (!searchTypes || !keys) return undefined;

	const upperCaseKeys = keys?.map((item) => item.toUpperCase());
	const newSearchTypes = searchTypes?.filter(
		(item) => !upperCaseKeys?.includes(item?.searchType)
	);
	const removedValues = searchTypes?.filter((item) =>
		upperCaseKeys.includes(item?.searchType)
	);

	const remainingSearchTypes = convertArrayToObject(removedValues);

	return {
		[keySearchNew]: newSearchTypes,
		...remainingSearchTypes,
	};
};

export const convertArrayToObject = (
	array: { searchType: string; searchValue?: unknown }[]
) => {
	return array?.reduce<Record<string, unknown>>((obj, item) => {
		if (item?.searchType) obj[toCamelCase(item.searchType)] = item?.searchValue;
		return obj;
	}, {});
};

export const truncateString = (str: string, maxLength: number): string => {
	if (str.length <= maxLength) return str;
	return str.slice(0, maxLength) + '...';
};

export const formatDetailTitle = (
	id: string | number = '',
	title: string = ''
) => {
	return {
		id: String(id),
		title: truncateString(title, MAX_LENGTH_DETAIL_TITLE),
	};
};

export const removeSuffixWord = (input: string, suffix: string[]): string => {
	if (!input || !suffix || suffix.length === 0) {
		return input || '';
	}

	let result = input;
	let hasSuffix = true;
	while (hasSuffix) {
		hasSuffix = false;
		for (const s of suffix) {
			if (result.endsWith(s)) {
				result = result.slice(0, -s.length).trim();
				hasSuffix = true;
			}
		}
	}
	return result;
};

export const removeNilOrEmptyObjDeep = (obj: any): any => {
	// Nếu obj là null hoặc undefined, trả về undefined
	if (obj === null || obj === undefined) {
		return undefined;
	}

	// Nếu obj là array, xử lý từng phần tử
	if (Array.isArray(obj)) {
		const filteredArr = obj
			.map((item) => removeNilOrEmptyObjDeep(item))
			.filter((item) => item !== undefined);
		return filteredArr.length ? filteredArr : undefined;
	}

	// Nếu obj là object
	if (typeof obj === 'object') {
		const newObj: Record<string, any> = {};
		let hasValidValues = false;

		Object.keys(obj).forEach((key) => {
			const value = removeNilOrEmptyObjDeep(obj[key]);
			if (value !== undefined) {
				newObj[key] = value;
				hasValidValues = true;
			}
		});

		return hasValidValues ? newObj : undefined;
	}

	if (obj === '') {
		return undefined;
	}

	return obj;
};

/**
 * Converts the number to Korean format (uses comma for thousands separator)
 * @param value - The value to format (can be a number, string, null, undefined, etc.)
 * @returns The string formatted in Korean style or the original value if invalid
 */

export function formatNumberKR(
	value: string | number | null | undefined
): string {
	if (value === null || value === undefined || value === '') {
		return '';
	}

	// If the value is already a number, format it.
	if (typeof value === 'number' && !isNaN(value)) {
		return value.toLocaleString('ko-KR');
	}

	// If input is string, remove ',' and convert to number
	if (typeof value === 'string') {
		const cleanedValue = value.replace(/,/g, ''); // Remove commas if present
		const numberValue = Number(cleanedValue);

		if (!isNaN(numberValue)) {
			return numberValue.toLocaleString('ko-KR');
		}
	}

	// If cannot be converted to a number, return the original value
	return String(value);
}

export const toBoolean = (value: any): boolean => {
	if (typeof value === 'boolean') return value;

	if (typeof value === 'string') {
		const normalized = value.trim().toLowerCase();
		return ['true', '1', 'yes', 'on'].includes(normalized);
	}

	if (typeof value === 'number') {
		return value !== 0;
	}

	return Boolean(value);
};

export const formatVisaCodeWithTime = (
	visaCode: ResidenceEnum,
	visaTime: VisaTimeEnum,
	addNewLine: boolean = false
) => {
	if (!visaCode) return '';

	const visaCodeName = ResidenceLabel[visaCode] ?? '';
	const visaTimeLabel = VisaTimeLabel[visaTime] ?? '';

	const separator = addNewLine ? '\n' : '';
	const visaCodeWithTime = `${visaCodeName}${visaTimeLabel ? `${separator}(${visaTimeLabel})` : ''}`;

	return visaCodeWithTime;
};

export const formatVisaTime = (visaTime: VisaTimeEnum) => {
	if (!visaTime) return '-';

	const visaTimeLabel = VisaTimeLabel[visaTime] ?? '-';

	const visaCodeWithTime = visaTimeLabel;

	return visaCodeWithTime;
};
